/**
 * Feedback form definitions.
 *
 * Each form is backed by its own table with one flat column per question, and
 * `field.id` MUST equal the column name — that single invariant is what lets
 * FeedbackFields stay generic while the insert payload stays flat:
 *
 *     supabase.from(form.table).insert({ user_id, ...values })
 *
 * Adding a form means a migration plus an entry here.
 */

export type FeedbackFieldType = "text" | "textarea" | "select" | "rating";

export type FeedbackField = {
  /** Must equal the column name. Postgres identifier rules apply. */
  id: string;
  label: string;
  type: FeedbackFieldType;
  placeholder?: string;
  /**
   * Must agree with whatever the table enforces for that column — a NOT NULL,
   * or a CHECK. `product_feedback` uses the latter (see
   * product_feedback_answers_required), because its questions became mandatory
   * after rows had already been collected under the old, optional rule.
   */
  required?: boolean;
  /** `select` only. */
  options?: string[];
  /** Should mirror the column's length CHECK, so the DB never has to reject. */
  maxLength?: number;
  /** `rating` only — inclusive bounds. */
  min?: number;
  max?: number;
  /** `rating` only — captions under the endpoints. */
  minLabel?: string;
  maxLabel?: string;
};

export type FeedbackFormDef = {
  /** Config identity, and the value cached in localStorage. */
  key: string;
  /** The Postgres table backing this form. */
  table: string;
  title: string;
  description?: string;
  fields: FeedbackField[];
};

// Annotated rather than `satisfies`: prettier is pinned at 2.6.2, which cannot
// parse `satisfies` and fails lint even though tsc is happy.
export const FEEDBACK_FORMS: Record<string, FeedbackFormDef> = {
  "product-feedback-2026-08": {
    key: "product-feedback-2026-08",
    table: "product_feedback",
    title: "How's Mindmap.so working for you?",
    description:
      "Four quick questions. Your answers go straight into what we build next.",
    fields: [
      {
        id: "expectations",
        label: "What do you expect in Mindmap.so?",
        type: "textarea",
        placeholder: "e.g., faster Notion sync, offline editing, templates",
        required: true,
        maxLength: 2000,
      },
      {
        id: "missing",
        label: "What are you missing right now?",
        type: "textarea",
        placeholder: "The one thing that would make this more useful to you",
        required: true,
        maxLength: 2000,
      },
      {
        id: "prior_tools",
        label: "What were you using before Mindmap.so?",
        type: "text",
        placeholder:
          "e.g., Miro, Whimsical, XMind, Notion pages, pen and paper",
        required: true,
        maxLength: 200,
      },
      // Deliberately no placeholder: this one sits directly under "what were
      // you using before", and examples here would steer the answer toward
      // whichever reason we happened to list.
      {
        id: "switch_reason",
        label: "What made you switch to Mindmap.so?",
        type: "textarea",
        required: true,
        maxLength: 2000,
      },
    ],
  },

  /**
   * The legacy onboarding gate. Its columns in `user_feedback` already match
   * the ids they need, so sharing the renderer needed no schema change. The
   * modal wrapping this one is deliberately non-dismissible; see FeedbackModal.
   */
  onboarding: {
    key: "onboarding",
    table: "user_feedback",
    title: "Help us shape the next version of Mindmap.so",
    description:
      "We're rebuilding Mindmap.so from the ground up. If you actually use mind " +
      "maps for real work, your input will directly influence what we build " +
      "next. Mindmap.so will always remain a free tool.",
    fields: [
      {
        id: "who_are_you",
        label: "What best describes you?",
        type: "text",
        placeholder:
          "e.g., Founder, student, PM, engineer, researcher, writer, other",
        required: true,
      },
      {
        id: "use_case",
        label: "How do you plan to use Mindmap.so along with Notion?",
        type: "textarea",
        placeholder: "e.g., use the same that you have currently",
        required: true,
      },
      {
        id: "referral_source",
        label: "Where did you first hear about Mindmap.so?",
        type: "text",
        placeholder: "e.g., Twitter, Notion marketplace, Google, ChatGPT",
        required: true,
      },
    ],
  },
};

/** The form the footer button currently opens. */
export const ACTIVE_FEEDBACK_FORM: FeedbackFormDef =
  FEEDBACK_FORMS["product-feedback-2026-08"];

export const ONBOARDING_FEEDBACK_FORM: FeedbackFormDef =
  FEEDBACK_FORMS.onboarding;
